import { fetchBannerConfig, sendConsent, sendImpression } from "./api";
import { BannerView } from "./banner";
import { BannerExtension } from "./extension";
import { loadTcfModule } from "./tcf-loader";
import { installTcfStub } from "./tcf-stub";
import { Blocker } from "./blocker";
import { ScriptConfig, findSdkScript, readScriptConfig } from "./config";
import {
  ConsentModeOptions,
  DEFAULT_CONSENT_MODE,
  setConsentDefaults,
  updateConsent,
} from "./consent-mode";
import { pickLanguage } from "./language";
import {
  StoredConsent,
  hasCategory,
  newVisitorId,
  readConsent,
  writeConsent,
} from "./storage";
import {
  ConsentAction,
  ConsentModeKind,
  GPC_REFUSED,
  PublicBannerConfig,
} from "./types";

/** window.BerryCookie: lets the site read the consent and reopen the banner. */
export interface BerryCookieApi {
  readonly version: string;
  readonly config: ScriptConfig | null;
  /** The visitor's current decision, or null before they decide. */
  readonly consent: Readonly<StoredConsent> | null;
  /** How consent works for this visitor ("optIn", "optOut", "none"); null until the banner loaded. */
  readonly mode: ConsentModeKind | null;
  /** True when the category is accepted ("necessary" always is). */
  hasConsent(category: string): boolean;
  /** Opens the preference center so the visitor can change their choice. */
  show(): void;
  /** Shows the first-layer banner again, for a "cookie settings" link. */
  renew(): void;
}

declare global {
  interface Window {
    BerryCookie?: BerryCookieApi;
  }
}

/** Fired on window after every decision; detail is the stored consent. */
export const CONSENT_EVENT = "berrycookie:consent";

export function start(win: Window, doc: Document): BerryCookieApi | undefined {
  // Browsers expose <script id="BerryCookie"> as window.BerryCookie, so check for our API, not just a value.
  const existing = win.BerryCookie;
  if (existing && typeof existing.hasConsent === "function") {
    // Loaded twice on the same page; keep the first instance.
    return existing;
  }

  const config = readScriptConfig(findSdkScript(doc));
  let consent = readConsent(doc);
  // Categories allowed without a decision: all of them where the rules are opt-out or no banner at all.
  let implied: string[] | null = null;
  const allowed = (category: string) =>
    consent
      ? hasCategory(consent, category)
      : category === "necessary" || (implied?.indexOf(category) ?? -1) >= 0;
  const effective = () => (consent ? consent.cats : (implied ?? ["necessary"]));

  // Vendors may call __tcfapi before the configuration arrives; the stub queues their calls.
  if (config?.tcf) {
    installTcfStub(win, doc);
  }
  let extension: BannerExtension | null = null;

  // Before anything else on the page runs: Consent Mode defaults and the tracker guard.
  if (config?.consentDefaults !== false) {
    setConsentDefaults(win as never, allowed);
  }
  const blocker = new Blocker(doc, config?.blockingMode !== "manual", allowed);
  blocker.start();
  blocker.release();

  let view: BannerView | null = null;
  let banner: PublicBannerConfig | null = null;
  let language = "en";
  let consentMode: ConsentModeOptions = DEFAULT_CONSENT_MODE;

  const decide = (action: ConsentAction, categories: string[]) => {
    if (!banner || !config) {
      return;
    }
    const previous = consent;
    const before = effective();
    const keys = banner.settings.categories.map((c) => c.key);
    consent = {
      id: previous?.id ?? newVisitorId(),
      v: banner.version,
      cats: categories.filter((c, i) => categories.indexOf(c) === i),
      all: keys,
      act: action,
      ts: Date.now(),
    };
    writeConsent(doc, consent, banner.settings.behavior.consentLifetimeMonths);
    updateConsent(win as never, allowed, consent.cats, consentMode);
    blocker.release();
    sendConsent(
      config.apiBaseUrl,
      config.publicKey,
      {
        visitorId: consent.id,
        bannerVersion: banner.version,
        action,
        categories: consent.cats,
        language,
        pageUrl: win.location.href.split("#")[0],
        tcString: extension?.tcString() ?? null,
      },
      config.countryOverride,
    );
    win.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: consent }));

    // Scripts that already run cannot be stopped; reload so a withdrawn category takes effect.
    if (
      (previous || implied) &&
      before.some((c) => consent!.cats.indexOf(c) < 0)
    ) {
      win.location.reload();
      return;
    }
    view?.showReopenButton(() =>
      view?.showPreferences(effective(), consent?.id),
    );
  };

  const api: BerryCookieApi = {
    version: __SDK_VERSION__,
    config,
    get consent() {
      return consent;
    },
    get mode() {
      return banner?.mode ?? null;
    },
    hasConsent: allowed,
    show: () => view?.showPreferences(effective(), consent?.id),
    renew: () => view?.showBanner(),
  };
  win.BerryCookie = api;

  if (!config) {
    console.warn(
      "[BerryCookie] Missing or invalid data-key on the script tag; the banner is disabled.",
    );
    return api;
  }

  void fetchBannerConfig(
    config.apiBaseUrl,
    config.publicKey,
    config.countryOverride,
  ).then(async (loaded) => {
    if (!loaded) {
      return;
    }
    banner = loaded;
    const settings = loaded.settings;
    const mode = loaded.mode ?? "optIn";
    consentMode = {
      ...DEFAULT_CONSENT_MODE,
      ...(settings.googleConsentMode ?? {}),
    };
    if (!config.googleConsentMode) {
      consentMode = { ...consentMode, enabled: false };
    }

    // Global Privacy Control (navigator.globalPrivacyControl): in opt-out regions (e.g. California, where the
    // CCPA rules require it) a new visitor's browser signal refuses selling/sharing, so marketing is never
    // implied, not even for a moment. Opt-in regions are unchanged: nothing optional runs before consent anyway.
    const gpc =
      !consent &&
      mode === "optOut" &&
      (win.navigator as Navigator & { globalPrivacyControl?: boolean })
        .globalPrivacyControl === true;

    // Opt-out regions and regions without a banner: everything runs until the visitor refuses.
    if (!consent && mode !== "optIn" && !gpc) {
      implied = settings.categories.map((c) => c.key);
      updateConsent(win as never, allowed, implied, consentMode);
      blocker.release();
    }
    if (mode === "none") {
      return;
    }

    const htmlLang = doc.documentElement.getAttribute("lang") ?? "";
    language = pickLanguage(
      Object.keys(settings.texts),
      [htmlLang, ...(win.navigator.languages ?? [win.navigator.language])],
      settings.defaultLanguage,
    );
    if (gpc) {
      // Recorded like a decision (act "GPC"), so it is in the consent log; the visitor can still change it.
      decide(
        "GPC",
        settings.categories
          .filter((c) => (c.enabled || c.required) && c.key !== GPC_REFUSED)
          .map((c) => c.key),
      );
    }
    if (loaded.tcf) {
      extension = await loadTcfModule(win, doc, config.scriptUrl, {
        win,
        doc,
        apiBaseUrl: config.apiBaseUrl,
        language,
        // The TCF applies where the GDPR does: where the banner asks first.
        gdprApplies: mode === "optIn",
        cmpId: loaded.tcf.cmpId,
        cmpVersion: loaded.tcf.cmpVersion,
        publisherCountry: loaded.tcf.publisherCountry,
        vendors: loaded.tcf.vendors,
        consentLifetimeMonths: settings.behavior.consentLifetimeMonths,
      });
    }
    view = new BannerView(
      doc,
      settings,
      settings.texts[language],
      { onDecision: decide },
      loaded.cookies ?? [],
      language,
      extension,
      loaded.branding ?? null,
      config.scriptUrl,
    );

    // Ask again when the banner gained a category the visitor never saw.
    const known = consent?.all ?? [];
    const needsDecision =
      !consent ||
      settings.categories.some((c) => c.enabled && known.indexOf(c.key) < 0) ||
      (extension !== null && !extension.hasDecision());
    if (needsDecision) {
      view.showBanner();
      sendImpression(config.apiBaseUrl, config.publicKey);
    } else {
      view.showReopenButton(() =>
        view?.showPreferences(effective(), consent?.id),
      );
    }
  });

  return api;
}

start(window, document);
